import { IconError } from "@/assets/icons/geist/IconError";
import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useIsUserAuthenticatedWithRunner } from "@/queries/runner-queries";
import { TrackLocations } from "@/hooks/use-segment";
import { type AgentMessageFragmentHostAuthenticationRequired } from "@/hooks/use-stream-conversation";
import { cn } from "@/components/podkit/lib/cn";
import { useState, type FC } from "react";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";

export const FragmentHostAuthentication: FC<{
    fragment: AgentMessageFragmentHostAuthenticationRequired;
}> = ({ fragment }) => {
    const repoUrl = "https://" + fragment.host;
    const { data: authCheckResponse } = useIsUserAuthenticatedWithRunner(fragment.runnerId, repoUrl, {
        refetchUntilAuthenticated: false,
        enabled: true,
    });
    const [showScmAuthModal, setShowScmAuthModal] = useState(false);

    if (!authCheckResponse) {
        return null;
    }

    const isAuthenticationRequired = authCheckResponse?.type === "AuthenticationRequired";
    return (
        <div className={cn("flex items-center gap-2 duration-700 animate-in fade-in")}>
            <div className="contents">
                {isAuthenticationRequired ? (
                    <IconError size="base" className="size-4 shrink-0 text-content-red" />
                ) : (
                    <IconCheckFill size="base" className="size-4 shrink-0 text-content-green" />
                )}
            </div>
            <Text className="text-base">
                {isAuthenticationRequired
                    ? `Authentication required for ${authCheckResponse?.scmName}`
                    : `Successfully authenticated with ${authCheckResponse?.scmName}`}
            </Text>
            {isAuthenticationRequired && (
                <Button
                    size="sm"
                    variant="primary"
                    className="ml-auto"
                    onClick={() => {
                        if (!authCheckResponse) {
                            return;
                        }
                        setShowScmAuthModal(true);
                    }}
                >
                    Authenticate
                </Button>
            )}
            {showScmAuthModal && isAuthenticationRequired && (
                <SCMAuthenticationModal
                    repoURL={repoUrl}
                    authenticationUrl={authCheckResponse.url}
                    patAuth={authCheckResponse.patAuth}
                    scmId={authCheckResponse.scmId}
                    scmName={authCheckResponse.scmName}
                    runnerId={fragment.runnerId}
                    onClose={() => setShowScmAuthModal(false)}
                    onClickContinue={() => setShowScmAuthModal(false)}
                    data-track-location={TrackLocations.AgentExecutionSCMAuthenticationModal}
                />
            )}
        </div>
    );
};
